import type { QueryClient } from '@tanstack/react-query';
import type { Session } from '@/api/client';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import { upsertReaderPlannedSessionsCache } from '@/lib/readerPlannedSessionsCache';

function sortSessions(sessions: Session[]): Session[] {
  return [...sessions].sort((a, b) => {
    const dateDiff = (a.date ?? '').localeCompare(b.date ?? '');
    if (dateDiff !== 0) return dateDiff;
    return (a.start ?? '').localeCompare(b.start ?? '');
  });
}

/**
 * Патч денормализованных полей проекта на сессиях в кэше расписания
 * (`project_name`, `project_type` приходят с JOIN при fetch, не колонки sessions).
 */
export function patchSessionsProjectFields(
  queryClient: QueryClient,
  projectId: string,
  fields: { project_name?: string | null; project_type?: string | null }
): void {
  const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
  for (const [queryKey, old] of entries) {
    if (!old) continue;
    if (!old.some((s) => s.project_id === projectId)) continue;

    const next = old.map((s) => {
      if (s.project_id !== projectId) return s;
      let changed = false;
      const patch: Partial<Pick<Session, 'project_name' | 'project_type'>> = {};
      if ('project_name' in fields && (s.project_name ?? null) !== (fields.project_name ?? null)) {
        patch.project_name = fields.project_name ?? null;
        changed = true;
      }
      if ('project_type' in fields && (s.project_type ?? null) !== (fields.project_type ?? null)) {
        patch.project_type = fields.project_type ?? null;
        changed = true;
      }
      return changed ? { ...s, ...patch } : s;
    });

    if (next.some((s, i) => s !== old[i])) {
      queryClient.setQueryData<Session[]>(queryKey, next);
    }
  }
}


function upsertProjectSessionsPopover(queryClient: QueryClient, session: Session): void {
  const targetProjectId = session.project_id;
  if (!targetProjectId) return;

  const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['projectSessions'] });
  for (const [queryKey, old] of entries) {
    if (!old) continue;
    const cacheProjectId = queryKey[1];
    const withoutSession = old.filter((item) => item.id !== session.id);

    if (cacheProjectId !== targetProjectId) {
      if (withoutSession.length !== old.length) {
        queryClient.setQueryData<Session[]>(queryKey, withoutSession);
      }
      continue;
    }

    const next = sortSessions([...withoutSession, session]);
    const unchanged = old.length === next.length && old.every((item, index) => item === next[index]);
    if (!unchanged) {
      queryClient.setQueryData<Session[]>(queryKey, next);
    }
  }
}

/** Вернуть сессию в кэш расписания после undo/redo (в т.ч. после восстановления удалённой). */
export function mergeSessionIntoScheduleCaches(
  queryClient: QueryClient,
  session: Session
): void {
  const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
  for (const [queryKey, old] of entries) {
    if (!old) continue;
    const qFrom = queryKey[1] as string | undefined;
    const qTo = queryKey[2] as string | undefined;
    if (!qFrom || !qTo) continue;
    if (session.date < qFrom || session.date > qTo) continue;

    const exists = old.some((s) => s.id === session.id);
    const next = exists
      ? old.map((s) => (s.id === session.id ? { ...s, ...session } : s))
      : sortSessions([...old, session]);
    queryClient.setQueryData<Session[]>(queryKey, next);
  }

  upsertProjectSessionsPopover(queryClient, session);
  upsertReaderPlannedSessionsCache(queryClient, session);

  if (session.project_id) {
    invalidateSummariesForProjectIds(queryClient, [session.project_id]);
  }
}

export function normalizeUndoSessionPayload(payload: unknown): Session | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;
  if (typeof raw.id !== 'string' || !raw.id) return null;

  const readers = Array.isArray(raw.readers) ? (raw.readers as Session['readers']) : undefined;
  const readerIdsFromReaders =
    readers
      ?.map((r) => {
        if (!r) return null;
        if (typeof r.id === 'string' && r.id) return r.id;
        const personId = (r as { person_id?: string }).person_id;
        return typeof personId === 'string' && personId ? personId : null;
      })
      .filter((id): id is string => typeof id === 'string' && id.length > 0) ?? [];
  const reader_ids = Array.isArray(raw.reader_ids)
    ? (raw.reader_ids as string[])
    : readerIdsFromReaders.length > 0
      ? readerIdsFromReaders
      : undefined;

  return {
    ...(raw as unknown as Session),
    readers,
    reader_ids,
    reader_id:
      typeof raw.reader_id === 'string'
        ? raw.reader_id
        : reader_ids?.[0] ?? null,
  };
}
